import { useEffect, useState } from 'react';
import { completeCallback } from './imsClient';
import { useToken } from './TokenContext';

/**
 * Renders at /auth/callback. Reads the code/state from the URL,
 * exchanges via PKCE, persists the session, and navigates to the
 * caller-supplied `returnTo` path. Anything that goes wrong surfaces
 * as an inline error with a "Try again" link back to home.
 *
 * The exchange is run exactly once — React 18's StrictMode will
 * double-invoke effects in dev, so we guard with a module-level
 * sentinel to avoid hitting `/ims/token/v3` twice with the same
 * authorization code (which would 400 on the second attempt).
 */

let exchangeStarted = false;

export function AuthCallback(): JSX.Element {
  const { setSession } = useToken();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (exchangeStarted) return;
    exchangeStarted = true;

    completeCallback()
      .then(({ session, returnTo }) => {
        setSession(session);
        // Replace so the user can't navigate back into the callback URL
        // (which would re-attempt the exchange and fail).
        window.history.replaceState({}, '', returnTo);
        // Force a re-render of the app shell now that we have a session.
        window.location.replace(returnTo);
      })
      .catch((e: unknown) => {
        const msg = e instanceof Error ? e.message : 'Sign-in failed.';
        setError(msg);
      });
    // We intentionally run this once — `setSession` from context is
    // stable and the module-level sentinel prevents StrictMode double-run.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (error) {
    return (
      <div className="signin-modal">
        <div className="signin-card">
          <h2>Sign-in failed</h2>
          <p className="signin-error">{error}</p>
          <button
            type="button"
            onClick={() => window.location.assign('/')}
            className="signin-button"
          >
            Back to home
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="signin-modal">
      <div className="signin-card">
        <p className="signin-subtitle">Completing sign-in…</p>
      </div>
    </div>
  );
}
